import { AdviceCardResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { WeatherOutlookDayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-outlook.response';
import { GreenLightVerdictResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/green-light/green-light.response';
import { GreenLightGesture, GreenLightReason, GreenLightStatus } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/green-light.enum';
import { AdviceTrigger } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/advice-trigger.enum';
import { LocationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/location-type.enum';
import { LineIconName } from '../../../@icons/line-icons';
import { labelText } from '../../../@commons/utils/label-text.util';
import { lastFileUrl } from '../../../garden/@components/last-file-url.util';
import { UserAlertResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/smart-alerts/user-alert.response';
import { formatVwDate } from '../../../@commons/pipes/vw-date.pipe';
import { NounNumber } from '../../../@commons/utils/noun-phrase.util';
import { WeatherCondition } from '../weather-moon-band/weather-moon-band.model';
import { DayAction, DayContext, DayFamily, DayPlant, DayText, DayTone, localDay } from './day-message.model';

/** Ce qu'une règle trouve dans les données : des plantes, des valeurs, une raison. */
export interface DayCandidate {
  code: string;
  plants: DayPlant[];
  /** Valeurs du signal, du titre et de la raison (`t`, `n`, `day`…). */
  params?: Record<string, string | number>;
  /** Variante du signal : `today`, `tomorrow`, `day`… */
  signalVariant?: string;
  /** Titre écrit par le serveur (alerte) : il remplace le gabarit long. */
  titleText?: string;
  reason?: DayText[];
  estimated?: boolean;
  eventDate?: string | null;
  link?: string | null;
  priority?: number;
  family?: DayFamily;
  tone?: DayTone;
  alertUid?: string;
  /** Le temps que ce candidat annonce, quand il diffère de celui de la règle (gel cette nuit, ou pas de gel). */
  announces?: WeatherCondition | null;
}

/**
 * Une règle du catalogue. Les textes vivent sous `home.day.msg.<id>` :
 * `title` (forme longue, les plantes nommées par leur grammaire : « Couvre
 * tes pommes de terre ce soir. »), `title-plain` (la même sans accord, quand
 * un nom n'est pas encore relu), `title-many` (trois plantes et plus),
 * `short` (forme courte), `reason` (raison par défaut) ; le signal sous
 * `home.day.signal.<id>[.<variante>]` ; le bouton sous `home.day.button.<clé>`.
 */
export interface DayMessageDef {
  id: string;
  family: DayFamily;
  priority: number;
  tone: DayTone;
  icon: LineIconName;
  /** Ordre dans la sécurité : le gel devant le vent, le vent devant le reste. */
  rank?: number;
  /** Fondée sur la prévision : rien sans météo à jour. */
  weather?: boolean;
  /** Le temps que son signal annonce (gel, vent…) : la bande météo le tait. Seule source de ce qui se tait. */
  announces?: WeatherCondition;
  /** Mois (1 à 12) où la règle a du sens ; toute l'année sans valeur. */
  months?: number[];
  /** Un même geste sur plusieurs plantes : un seul message. */
  group?: boolean;
  /** Le geste change d'une culture à l'autre : un message par culture. */
  groupBy?: 'cultivation';
  /** Parle de tout le jardin : il ne côtoie pas un message qui nomme une plante. */
  wholeGarden?: boolean;
  estimated?: boolean;
  /** La raison par défaut (`home.day.msg.<id>.reason`) suit celle de la règle. */
  defaultReason?: boolean;
  /** Le titre et la raison se comptent (`_one`) : « Ta plante… », « Tes 4 plantes… ». */
  counted?: boolean;
  /** En ligne de geste, la raison courte (`home.day.msg.<id>.row`) plutôt que le lieu de la plante. */
  rowReason?: boolean;
  /** Une seule fois sur cet appareil. */
  once?: boolean;
  /** Le nombre des plantes dans le titre : pluriel par défaut (« tes fèves »), singulier pour « ton ail ». */
  number?: NounNumber;
  /** Le signal et la pastille d'une autre règle : les gels de cette nuit disent tous « Gel cette nuit ». */
  like?: string;
  /** Les règles moins précises dont celle-ci reprend les plantes : « Couvre tes pommes de terre » plutôt que le gel général. */
  overrides?: string[];
  button?: { key: string; action: DayAction };
  collect(ctx: DayContext): DayCandidate[];
}

/** Seuils de la prévision : gel (minimale), chaleur (maximale), vent (rafales). */
export const FROST_C = 2;
const HEAT_C = 30;
export const WIND_KMH = 50;
const HEAVY_RAIN_MM = 15;
const WINTER = [11, 12, 1];

/** Les jours qui viennent : aujourd'hui (0), demain (1), puis les suivants. */
export function forecast(ctx: DayContext): WeatherOutlookDayResponse[] {
  const today = localDay(ctx.now);
  return (ctx.advice?.forecast ?? []).filter((day) => day.date >= today);
}

export function when(index: number): 'today' | 'tomorrow' | 'day' {
  return index === 0 ? 'today' : index === 1 ? 'tomorrow' : 'day';
}

/** « mercredi » : le jour de la semaine dans la langue de l'app. */
export function weekday(ctx: DayContext, date: string): string {
  return formatVwDate(date, 'weekday', ctx.lang);
}

export function plantedAt(ctx: DayContext, uid: string | null): string | null {
  const plant = uid ? ctx.plants?.find((p) => p.uid === uid) : undefined;
  return plant?.plantedDate ? new Date(plant.plantedDate).toISOString() : null;
}

/** Le nom de la culture dans une phrase : celui de la plante du jardin, sinon celui d'un sachet de la même culture. */
export function grammarOf(ctx: DayContext, plantUid: string | null, cultivationUid?: string | null) {
  const plants = ctx.advice?.plants ?? [];
  const byPlant = plantUid ? plants.find((p) => p.plantUid === plantUid) : undefined;
  if (byPlant) return byPlant.grammar;
  if (!cultivationUid) return null;
  return (plants.find((p) => p.cultivationUid === cultivationUid) ?? ctx.advice?.seeds?.find((s) => s.cultivationUid === cultivationUid))?.grammar ?? null;
}

/** Les cartes du serveur d'un déclencheur (`advices[]`), une plante chacune. */
function cards(ctx: DayContext, trigger: AdviceTrigger): AdviceCardResponse[] {
  return (ctx.advice?.advices ?? []).filter((card) => card.trigger === trigger);
}

function cardPlant(ctx: DayContext, card: AdviceCardResponse): DayPlant {
  return {
    uid: card.plantUid,
    name: card.plantName,
    imageUrl: card.imageUrl,
    location: card.locationName,
    plantedAt: plantedAt(ctx, card.plantUid),
    plant: ctx.plants?.find((p) => p.uid === card.plantUid),
    grammar: grammarOf(ctx, card.plantUid),
  };
}

/** Une carte du serveur devient un candidat : sa plante, le lieu et son conseil en raison. */
export function fromCards(
  ctx: DayContext,
  trigger: AdviceTrigger,
  extra: Omit<DayCandidate, 'code' | 'plants'>,
): DayCandidate[] {
  return cards(ctx, trigger).map((card) => ({
    code: trigger,
    plants: [cardPlant(ctx, card)],
    reason: [
      ...(card.locationName ? [{ key: 'home.day.reason.location', params: { location: card.locationName } }] : []),
      // Le stade vient des degrés-jours : on le dit au conditionnel, avec sa pastille.
      ...(card.estimated ? [{ key: 'home.day.reason.estimated-stage', estimated: true }] : []),
      { text: card.text },
    ],
    estimated: card.estimated,
    ...extra,
  }));
}

function verdictPlant(ctx: DayContext, verdict: GreenLightVerdictResponse): DayPlant {
  return {
    uid: null,
    name: labelText(verdict.cultivation?.name as any, ctx.lang),
    imageUrl: lastFileUrl(verdict.cultivation, '') || null,
    location: null,
    grammar: grammarOf(ctx, null, verdict.cultivation?.uid),
  };
}

function verdicts(ctx: DayContext, test: (v: GreenLightVerdictResponse) => boolean): GreenLightVerdictResponse[] {
  return ctx.greenLights.filter((v) => (v.inGarden || v.inPlan || v.inSeeds) && test(v));
}

export function fromVerdicts(ctx: DayContext, code: string, test: (v: GreenLightVerdictResponse) => boolean, reason?: (v: GreenLightVerdictResponse) => DayText[]): DayCandidate[] {
  return verdicts(ctx, test).map((v) => ({
    code,
    plants: [verdictPlant(ctx, v)],
    reason: reason?.(v),
    eventDate: v.windowEndDate ?? v.date ?? null,
    link: `/feu-vert/${v.cropCode}`,
  }));
}

/** Plantes vivantes dehors (jardin, bac, terrasse, balcon). */
function outdoorPlants(ctx: DayContext) {
  const outside: string[] = [LocationType.GARDEN, LocationType.PLANTER_BOX, LocationType.TERRACE, LocationType.BALCONY];
  return (ctx.plants ?? []).filter((p) => !p.endedAt && outside.includes((p.location as any)?.locationType));
}

/** Une alerte plus vieille que deux jours n'est plus de la sécurité du jour. */
const ALERT_FRESHNESS_MS = 48 * 60 * 60 * 1000;
/** Les alertes au rang de message : urgentes ou à surveiller. */
const SAFETY_SEVERITIES = ['CRITICAL', 'WARNING'];
/** Deux alertes au plus parmi les messages : au-delà, la page Alertes. */
const MAX_ALERTS = 2;

/** Les alertes du jour qui demandent un geste : non lues, récentes, urgentes d'abord puis la plus récente. */
export function safetyAlerts(alerts: UserAlertResponse[] | null | undefined, now = new Date()): UserAlertResponse[] {
  return (alerts ?? [])
    .filter((alert) => alert.status === 'UNREAD' && SAFETY_SEVERITIES.includes(alert.severity))
    .filter((alert) => now.getTime() - new Date(alert.createTs).getTime() <= ALERT_FRESHNESS_MS)
    .sort((a, b) => {
      const urgency = Number(b.severity === 'CRITICAL') - Number(a.severity === 'CRITICAL');
      return urgency || new Date(b.createTs).getTime() - new Date(a.createTs).getTime();
    })
    .slice(0, MAX_ALERTS);
}

/** Une alerte du serveur, en message : son titre, sa consigne, sa plante. */
function fromAlerts(ctx: DayContext): DayCandidate[] {
  return safetyAlerts(ctx.alerts, ctx.now).map((alert) => {
    const critical = alert.severity === 'CRITICAL';
    const weather = alert.category === 'WEATHER';
    const plantUid: string | null = alert.metadata?.['plantUid'] ?? null;
    const plant = plantUid ? ctx.plants?.find((p) => p.uid === plantUid) : undefined;
    return {
      code: alert.ruleCode,
      plants: plant
        ? [{ uid: plant.uid, name: labelText(plant.cultivationVariant?.name as any, ctx.lang), imageUrl: lastFileUrl(plant.cultivationVariant, '') || null, location: plant.location?.name ?? null, plant, grammar: grammarOf(ctx, plant.uid) }]
        : [],
      titleText: labelText(alert.title as any, ctx.lang),
      reason: [{ text: labelText(alert.body as any, ctx.lang) }, ...(alert.actionSuggestion ? [{ text: labelText(alert.actionSuggestion as any, ctx.lang) }] : [])],
      priority: critical ? (weather ? 1 : 2) : 3,
      family: weather ? 'securite' : alert.category === 'DISEASE' ? 'maladie' : 'entretien',
      tone: weather ? 'frost' : 'alert',
      link: alert.deepLink ?? '/notifications',
      alertUid: alert.uid,
      eventDate: alert.createTs?.slice(0, 10) ?? null,
    };
  });
}

/**
 * Le catalogue du mot du jour, dans l'ordre où il se lit : la sécurité, l'eau,
 * le sol et les semis, la récolte, la croissance, puis les jours calmes. Ici
 * les règles des cartes du serveur, du Feu vert, des alertes et des totaux ;
 * celles qui partent des plantes et des sachets sont dans
 * `day-message.plant-rules.ts`. Une règle n'entre que si l'accueil a déjà ses
 * données ; celles qui en attendent de nouvelles restent dans
 * `catalogue-mot-du-jour.csv`.
 */
export const DAY_MESSAGES: DayMessageDef[] = [
  {
    // Gel cette nuit : la plus basse de demain matin (`forecast[1]`). Celle d'aujourd'hui est passée.
    id: 'FROST_TONIGHT', family: 'securite', priority: 1, rank: 1, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    overrides: ['GEL_AU_SOL'],
    button: { key: 'covered', action: 'DONE' },
    collect: (ctx) => {
      const night = forecast(ctx)[1];
      if (night?.tempMin == null || night.tempMin > FROST_C) return [];
      return fromCards(ctx, AdviceTrigger.FROST, { params: { t: Math.round(night.tempMin) }, eventDate: night.date });
    },
  },
  {
    // Gel plus tard dans la fenêtre : on prépare, sans urgence de ce soir.
    id: 'FROST_LATER', family: 'securite', priority: 3, rank: 2, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const days = forecast(ctx);
      if (days[1]?.tempMin != null && days[1].tempMin <= FROST_C) return [];
      const index = days.findIndex((d, i) => i >= 2 && d.tempMin != null && d.tempMin <= FROST_C);
      if (index < 0) return [];
      return fromCards(ctx, AdviceTrigger.FROST, {
        params: { t: Math.round(days[index].tempMin!), day: weekday(ctx, days[index - 1].date) },
        eventDate: days[index].date,
      });
    },
  },
  {
    id: 'STRONG_WIND', family: 'securite', priority: 1, rank: 3, tone: 'wind', icon: 'wind', weather: true, announces: 'WIND', group: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const days = forecast(ctx);
      const index = days.findIndex((d) => (d.windSpeed ?? 0) >= WIND_KMH);
      if (index < 0) return [];
      return fromCards(ctx, AdviceTrigger.STRONG_WIND, {
        signalVariant: when(index),
        params: { n: Math.round(days[index].windSpeed!), day: weekday(ctx, days[index].date) },
        eventDate: days[index].date,
      });
    },
  },
  {
    // Les alertes du serveur (mildiou, oïdium, gel) : leur titre et leur consigne.
    id: 'ALERT', family: 'maladie', priority: 2, rank: 4, tone: 'alert', icon: 'alert',
    button: { key: 'done', action: 'DONE' },
    collect: fromAlerts,
  },
  {
    id: 'HEATWAVE', family: 'meteo', priority: 2, tone: 'water', icon: 'sun', weather: true, announces: 'HOT', group: true,
    button: { key: 'watered', action: 'DONE' },
    collect: (ctx) => {
      const days = forecast(ctx);
      const index = days.findIndex((d) => (d.tempMax ?? -99) >= HEAT_C);
      if (index < 0) return [];
      return fromCards(ctx, AdviceTrigger.HEATWAVE, {
        signalVariant: when(index),
        params: { t: Math.round(days[index].tempMax!), day: weekday(ctx, days[index].date) },
        eventDate: days[index].date,
      });
    },
  },
  {
    id: 'DRY_SPELL', family: 'eau', priority: 2, tone: 'water', icon: 'droplet', weather: true, announces: 'DRY', group: true,
    button: { key: 'watered', action: 'DONE' },
    collect: (ctx) => {
      const rain = ctx.advice?.lastRain;
      const days = rain ? Math.round((new Date(localDay(ctx.now)).getTime() - new Date(rain.date).getTime()) / 86_400_000) : 0;
      return fromCards(ctx, AdviceTrigger.DRY_SPELL, rain && days > 1 ? { signalVariant: 'since', params: { n: days } } : { signalVariant: 'dry' });
    },
  },
  {
    id: 'HEAVY_RAIN', family: 'meteo', priority: 3, tone: 'water', icon: 'droplet', weather: true, announces: 'RAIN', group: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const mm = forecast(ctx).slice(0, 2).reduce((sum, d) => sum + (d.precipitation ?? 0), 0);
      if (mm < HEAVY_RAIN_MM) return [];
      return fromCards(ctx, AdviceTrigger.HEAVY_RAIN, { params: { mm: Math.round(mm) } });
    },
  },
  {
    id: 'FEU_VERT_SEMER', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', group: true, estimated: true, rowReason: true,
    button: { key: 'sown', action: 'DONE' },
    collect: (ctx) =>
      fromVerdicts(ctx, 'FEU_VERT_SEMER', (v) => v.status === GreenLightStatus.GO && v.gesture === GreenLightGesture.SOW, (v) => [
        { key: 'home.day.msg.FEU_VERT_SEMER.reason' },
        ...(v.inSeeds ? [{ key: 'home.day.reason.has-seeds' }] : []),
      ]),
  },
  {
    id: 'FEU_VERT_PLANTER', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', group: true, defaultReason: true, rowReason: true,
    button: { key: 'planted', action: 'DONE' },
    collect: (ctx) =>
      fromVerdicts(ctx, 'FEU_VERT_PLANTER', (v) => v.status === GreenLightStatus.GO && v.gesture !== GreenLightGesture.SOW),
  },
  {
    id: 'DERNIER_SEMIS', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', group: true, defaultReason: true, rowReason: true,
    button: { key: 'sown', action: 'DONE' },
    collect: (ctx) => fromVerdicts(ctx, 'DERNIER_SEMIS', (v) => v.status === GreenLightStatus.LAST_CHANCE),
  },
  {
    id: 'SEMER_AU_CHAUD', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', group: true, defaultReason: true, rowReason: true,
    button: { key: 'sown', action: 'DONE' },
    collect: (ctx) => fromVerdicts(ctx, 'SEMER_AU_CHAUD', (v) => v.status === GreenLightStatus.INDOOR),
  },
  {
    // Le Feu vert attend la terre : elle est détrempée.
    id: 'TERRE_MOUILLEE', family: 'sol', priority: 3, tone: 'water', icon: 'droplet', group: true, estimated: true, rowReason: true,
    collect: (ctx) =>
      fromVerdicts(ctx, 'TERRE_MOUILLEE', (v) => v.reason === GreenLightReason.WET_SOIL, (v) => [
        ...(v.rainYesterdayMm ? [{ key: 'home.day.reason.rain-yesterday', params: { mm: Math.round(v.rainYesterdayMm) } }] : []),
        { key: 'home.day.msg.TERRE_MOUILLEE.reason' },
      ]),
  },
  {
    id: 'RECOLTE_PRETE', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', group: true, estimated: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) =>
      (ctx.advice?.harvestSuggestions ?? []).map((h) => ({
        code: h.triggerCode || 'RECOLTE_PRETE',
        plants: [{
          uid: h.plantUid, name: h.name, imageUrl: h.imageUrl, location: h.locationName,
          plantedAt: plantedAt(ctx, h.plantUid), plant: ctx.plants?.find((p) => p.uid === h.plantUid),
          grammar: grammarOf(ctx, h.plantUid),
        }],
        reason: [
          ...(h.locationName ? [{ key: 'home.day.reason.location', params: { location: h.locationName } }] : []),
          { key: 'home.day.msg.RECOLTE_PRETE.reason' },
        ],
      })),
  },
  {
    // Une étape manuelle finie (semis, plantation) : la personne confirme sur la fiche.
    id: 'CONFIRMER_PLANTATION', family: 'croissance', priority: 5, tone: 'calm', icon: 'sprout',
    button: { key: 'open-plant', action: 'OPEN' },
    collect: (ctx) =>
      ctx.plantActions.map(({ plant, nextStep }) => ({
        code: 'CONFIRMER_PLANTATION',
        plants: [{
          uid: plant.uid, name: labelText(plant.cultivationVariant?.name as any, ctx.lang),
          imageUrl: lastFileUrl(plant.cultivationVariant, '') || null, location: plant.location?.name ?? null,
          plantedAt: plantedAt(ctx, plant.uid), plant, grammar: grammarOf(ctx, plant.uid),
        }],
        reason: [
          { key: 'home.day.reason.next-step', params: { step: labelText(nextStep.name as any, ctx.lang) } },
          { key: 'home.day.msg.CONFIRMER_PLANTATION.reason' },
        ],
        link: `/garden/plants/${plant.uid}?action=confirm-step`,
      })),
  },
  {
    id: 'PAUSE_FROID', family: 'croissance', priority: 6, tone: 'calm', icon: 'snowflake', group: true, estimated: true, defaultReason: true,
    collect: (ctx) => thermalWith(ctx, 'PAUSE_FROID', ['PAUSE_FROID', 'RALENTI_FROID']),
  },
  {
    id: 'PAUSE_CHALEUR', family: 'croissance', priority: 6, tone: 'calm', icon: 'sun', group: true, estimated: true, defaultReason: true,
    collect: (ctx) => thermalWith(ctx, 'PAUSE_CHALEUR', ['PAUSE_CHALEUR', 'RALENTI_CHALEUR']),
  },
  {
    // Moins de 10 heures de jour : presque rien ne pousse dehors.
    id: 'JOURS_COURTS', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', months: WINTER, defaultReason: true, counted: true, wholeGarden: true,
    collect: (ctx) => {
      const plants = outdoorPlants(ctx);
      return plants.length ? [{ code: 'JOURS_COURTS', plants: [], params: { n: plants.length } }] : [];
    },
  },
  {
    // Le repli des jours calmes : il part de l'état des plantes, pas de la météo.
    id: 'RIEN_DE_SPECIAL', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', defaultReason: true, counted: true, wholeGarden: true,
    collect: (ctx) => {
      const n = (ctx.plants ?? []).filter((p) => !p.endedAt).length;
      return n ? [{ code: 'RIEN_DE_SPECIAL', plants: [], params: { n } }] : [];
    },
  },
  {
    id: 'POTAGER_VIDE', family: 'hors_saison', priority: 7, tone: 'calm', icon: 'leaf', months: [10, 11, 12, 1, 2], defaultReason: true,
    button: { key: 'add-seed', action: 'OPEN' },
    collect: (ctx) =>
      ctx.counts && ctx.counts.plants === 0 && (ctx.counts.zones > 0 || ctx.counts.seeds > 0)
        ? [{ code: 'POTAGER_VIDE', plants: [], link: '/garden/add-seeds/select' }]
        : [],
  },
  {
    id: 'GRAINES_SEULES', family: 'premier_pas', priority: 7, tone: 'calm', icon: 'sprout', defaultReason: true, counted: true,
    collect: (ctx) =>
      ctx.counts && ctx.counts.plants === 0 && ctx.counts.seeds > 0
        ? [{ code: 'GRAINES_SEULES', plants: [], params: { n: ctx.counts.seeds } }]
        : [],
  },
  {
    // « Pour bien démarrer », sans charge : une seule fois, et seulement un jour sans geste.
    id: 'ALERTES_GEL_OFF', family: 'premier_pas', priority: 7, tone: 'calm', icon: 'snowflake', defaultReason: true, once: true,
    button: { key: 'enable-alerts', action: 'ALERTS' },
    collect: (ctx) => (ctx.setup?.kind === 'alerts' && !ctx.setup.done ? [{ code: 'ALERTES_GEL_OFF', plants: [] }] : []),
  },
  {
    id: 'INSTALLER_APP', family: 'premier_pas', priority: 7, tone: 'calm', icon: 'sprout', defaultReason: true, once: true,
    button: { key: 'install-app', action: 'INSTALL' },
    collect: (ctx) => (ctx.setup?.kind === 'install' && !ctx.setup.done ? [{ code: 'INSTALLER_APP', plants: [] }] : []),
  },
  {
    id: 'ZONE_VIDE', family: 'premier_pas', priority: 7, tone: 'calm', icon: 'sprout', months: [3, 4, 5, 6, 7, 8, 9, 10], defaultReason: true,
    button: { key: 'add-plant', action: 'OPEN' },
    collect: (ctx) =>
      ctx.counts && ctx.counts.zones > 0 && ctx.counts.plants === 0 && ctx.counts.seeds === 0
        ? [{ code: 'ZONE_VIDE', plants: [], link: '/garden/add-plant' }]
        : [],
  },
];

function thermalWith(ctx: DayContext, code: string, badges: string[]): DayCandidate[] {
  return ctx.thermal
    .filter((item) => item.conditionBadge && badges.includes(item.conditionBadge))
    .map((item) => ({
      code,
      plants: [{
        uid: item.plantUid, name: item.cultivationName, imageUrl: null, location: item.locationName || null,
        plantedAt: plantedAt(ctx, item.plantUid), plant: ctx.plants?.find((p) => p.uid === item.plantUid),
        grammar: grammarOf(ctx, item.plantUid),
      }],
    }));
}
